import React, { useCallback, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';

import {
  anchorReferenceFromElement,
  HOVER_POPOVER_CLOSE_MS,
  HOVER_POPOVER_OPEN_MS,
  type HoverPopoverAnchorReference,
  type HoverPopoverTiming,
  type Placement,
} from './HoverPopover.types';
import { HoverPopoverSurface } from './HoverPopoverSurface';
import { useHoverIntent } from './useHoverIntent';

interface HoverPopoverProps extends HoverPopoverTiming {
  /** What to show inside the floating card. */
  content: React.ReactNode;
  /** The trigger — wrapped in an inline-flex span that carries the hover/focus handlers. */
  children: React.ReactNode;
  placement?: Placement;
  offsetPx?: number;
  /** Don't open on touch devices (no true hover). Default true. */
  disableOnTouch?: boolean;
  role?: 'dialog' | 'tooltip';
}

/**
 * Self-contained hover popover: wrap a trigger, get a floating card on hover
 * (after a rest) that stays open while the pointer is over either the trigger
 * or the card, and also opens on keyboard focus for accessibility. Built on the
 * same floating-ui surface as the calendar's appointment card.
 *
 * For the "one card, many triggers" calendar case, use useKeyedHoverPopover
 * with <HoverPopoverSurface> directly instead.
 */
export const HoverPopover = ({
  content,
  children,
  placement = 'top',
  offsetPx = 8,
  openDelayMs = HOVER_POPOVER_OPEN_MS,
  closeDelayMs = HOVER_POPOVER_CLOSE_MS,
  disableOnTouch = true,
  role = 'dialog',
}: HoverPopoverProps) => {
  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [anchorReference, setAnchorReference] =
    useState<HoverPopoverAnchorReference | null>(null);
  const { scheduleOpen, scheduleClose, cancelClose, clearAll } = useHoverIntent();

  const open = useCallback(() => {
    if (triggerRef.current) setAnchorReference(anchorReferenceFromElement(triggerRef.current));
  }, []);

  const close = useCallback(() => {
    clearAll();
    setAnchorReference(null);
  }, [clearAll]);

  const onEnter = useCallback(() => {
    if (disableOnTouch && isTouch) return;
    cancelClose();
    scheduleOpen(open, openDelayMs);
  }, [disableOnTouch, isTouch, cancelClose, scheduleOpen, open, openDelayMs]);

  const onLeave = useCallback(() => {
    scheduleClose(close, closeDelayMs);
  }, [scheduleClose, close, closeDelayMs]);

  return (
    <>
      <Box
        component="span"
        ref={triggerRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={open}
        onBlur={close}
        sx={{ display: 'inline-flex' }}
      >
        {children}
      </Box>
      {anchorReference && (
        <HoverPopoverSurface
          open
          anchorReference={anchorReference}
          onClose={close}
          onPointerEnter={cancelClose}
          onPointerLeave={onLeave}
          placement={placement}
          offsetPx={offsetPx}
          role={role}
        >
          {content}
        </HoverPopoverSurface>
      )}
    </>
  );
};

export type { HoverPopoverProps };
